import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Copy, Check, Sparkles, Coins, Users, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/referrals/me").then((r) => setData(r.data)).catch(() => {}),
      api.get("/referrals/leaderboard").then((r) => setBoard(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareLink = data
    ? `${window.location.origin}/browse?ref=${data.referral_code}`
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Long-press to copy manually.");
    }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-muted_ink">Loading…</div>;
  if (!data) return null;

  return (
    <div data-testid="referrals-page" className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <div className="label-eyebrow text-sage mb-3">Account</div>
      <h1 className="font-serif text-5xl sm:text-6xl tracking-tight">Share Folio.</h1>
      <p className="text-muted_ink mt-4 max-w-xl text-lg">
        Give friends <span className="text-terracotta font-medium">{data.discount_percent}% off</span> their
        first ebook. When they buy, you earn <span className="text-terracotta font-medium">{data.discount_percent}%</span> back as Folio credit.
      </p>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
        <div data-testid="stat-credits" className="border border-edge bg-white p-6 flex items-start gap-4">
          <Coins className="w-5 h-5 text-terracotta mt-1" strokeWidth={1.5} />
          <div>
            <div className="label-eyebrow">Credit balance</div>
            <div className="font-serif text-4xl mt-2">${data.credits.toFixed(2)}</div>
            <div className="text-xs text-muted_ink mt-1">Auto-applied at checkout</div>
          </div>
        </div>
        <div data-testid="stat-conversions" className="border border-edge bg-white p-6 flex items-start gap-4">
          <Users className="w-5 h-5 text-terracotta mt-1" strokeWidth={1.5} />
          <div>
            <div className="label-eyebrow">Friends converted</div>
            <div className="font-serif text-4xl mt-2">{data.conversions}</div>
            <div className="text-xs text-muted_ink mt-1">Successful referrals</div>
          </div>
        </div>
        <div data-testid="stat-earned" className="border border-edge bg-white p-6 flex items-start gap-4">
          <Sparkles className="w-5 h-5 text-terracotta mt-1" strokeWidth={1.5} />
          <div>
            <div className="label-eyebrow">Lifetime earned</div>
            <div className="font-serif text-4xl mt-2">${data.total_earned.toFixed(2)}</div>
            <div className="text-xs text-muted_ink mt-1">From referrals</div>
          </div>
        </div>
      </div>

      {/* Share card */}
      <div className="mt-12 border border-edge bg-linen p-8 md:p-12">
        <div className="label-eyebrow text-sage">Your referral code</div>
        <div
          data-testid="referral-code"
          className="font-mono text-5xl md:text-6xl tracking-[0.2em] mt-3 text-ink select-all"
        >
          {data.referral_code}
        </div>

        <div className="mt-8">
          <label className="label-eyebrow block mb-2">Shareable link</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="referral-link"
              readOnly
              value={shareLink}
              className="flex-1 border border-edge bg-white px-4 py-3 text-sm font-mono text-muted_ink focus:outline-none"
            />
            <button
              data-testid="copy-link-btn"
              onClick={copyLink}
              className="btn-primary !py-3 flex items-center gap-2"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          {[
            { n: "01", t: "Share the link", d: "Send your code or link to a friend." },
            { n: "02", t: "They get 15% off", d: "Their first ebook purchase is discounted." },
            { n: "03", t: "You earn credit", d: "15% back to spend on your next book." },
          ].map((s) => (
            <div key={s.n} className="border-t border-edge pt-4">
              <div className="label-eyebrow text-terracotta">{s.n}</div>
              <div className="font-serif text-xl mt-1">{s.t}</div>
              <div className="text-muted_ink mt-1">{s.d}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 text-sm text-muted_ink">
        Tip: anyone visiting <Link to="/browse" className="text-ink underline underline-offset-4 decoration-terracotta">Folio</Link> through your link gets the discount automatically applied at checkout.
        {data.has_used_referral && (
          <span className="block mt-2">
            You&apos;ve already used a friend&apos;s code, so you can&apos;t be referred — but your code still works for everyone else.
          </span>
        )}
      </div>

      {/* Leaderboard */}
      {board && board.leaderboard && board.leaderboard.length > 0 && (
        <div data-testid="referrals-leaderboard" className="mt-16">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="label-eyebrow text-sage flex items-center gap-2">
                <Trophy className="w-3.5 h-3.5" /> Top referrers
              </div>
              <h2 className="font-serif text-3xl mt-2">This month, {board.month_label}</h2>
            </div>
          </div>
          <div className="border border-edge bg-white">
            {board.leaderboard.map((row) => {
              const medal = row.rank === 1 ? "🥇" : row.rank === 2 ? "🥈" : row.rank === 3 ? "🥉" : "";
              return (
                <div key={row.rank} data-testid={`leaderboard-rank-${row.rank}`}
                     className={`grid grid-cols-12 px-6 py-4 border-b border-edge last:border-b-0 items-center ${row.rank === 1 ? "bg-linen" : ""}`}>
                  <div className="col-span-2 font-serif text-3xl text-terracotta">
                    {medal || `#${row.rank}`}
                  </div>
                  <div className="col-span-6">
                    <div className="font-serif text-xl">{row.name}</div>
                    <div className="text-xs text-muted_ink">{row.conversions} friend{row.conversions === 1 ? "" : "s"} converted</div>
                  </div>
                  <div className="col-span-4 text-right">
                    <div className="font-serif text-2xl">${row.earned.toFixed(2)}</div>
                    <div className="text-xs text-muted_ink">earned</div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted_ink mt-3">
            The board resets on the first of every month. Names shown with a first-name-and-initial for privacy.
          </p>
        </div>
      )}
    </div>
  );
}
